import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import { useMyAdminRoles } from '@trello/business-logic-react/member';
import {
  isAdminOfOrganization,
  isMemberOfOrganization,
} from '@trello/business-logic/organization';
import { sendErrorEvent } from '@trello/error-reporting';
import { ApolloProvider } from '@trello/graphql';
import { Popover, PopoverScreen, usePopover } from '@trello/nachos/popover';
import { PopoverMenuButton } from '@trello/nachos/popover-menu';
import type { MenuProps } from '@trello/nachos/select';
import { Select, SelectComponents } from '@trello/nachos/select';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import { CreateNewCollection } from './CreateNewCollection';
import { EmptyState } from './EmptyState';
import { useOrgMembershipQuery } from './OrgMembershipQuery.generated';
import { useTagsQuery } from './TagsQuery.generated';

import styles from './CollectionsSelect.less';

export interface CollectionOption {
  label: string;
  value: string;
}

const CollectionsScreen = {
  EmptyState: 0,
  CreateNewCollection: 1,
  SoloCreateNewCollection: 2,
} as const;

const Menu = (props: MenuProps<CollectionOption, false>) => {
  const onClearClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'dropdownItem',
      actionSubjectId: 'clearFilterDropdownItem',
      source: 'workspaceBoardsScreen',
      containers: {
        organization: {
          id: props.selectProps.orgId,
        },
      },
    });
    props.selectProps.onClearClick();
  }, [props.selectProps]);

  const onCreateClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'dropdownItem',
      actionSubjectId: 'createCollectionDropdownItem',
      source: 'workspaceBoardsScreen',
      containers: {
        organization: {
          id: props.selectProps.orgId,
        },
      },
    });
    props.selectProps.onCreateClick();
  }, [props.selectProps]);

  if (props.selectProps.options.length === 0) {
    return (
      <SelectComponents.Menu
        className={cx(styles.menu, styles.emptyState)}
        {...props}
      >
        <header className={styles.emptyStateHeader}>
          <h2>
            <FormattedMessage
              id="templates.workspace_page.collections"
              defaultMessage="Collections"
              description="The title of collection"
            />
          </h2>
        </header>
        <EmptyState
          collectionsEnabled={props.selectProps.tagsFeatureEnabled}
          onCreateClick={props.selectProps.onCreateClick}
          orgId={props.selectProps.orgId}
          orgName={props.selectProps.orgName}
          isMember={props.selectProps.isMemberOfOrg}
          isAdmin={props.selectProps.isAdminOfOrg}
        />
      </SelectComponents.Menu>
    );
  }

  return (
    <SelectComponents.Menu className={styles.menu} {...props}>
      <>
        {props.children}
        {props.selectProps.value && (
          <div className={styles.menuButton}>
            <PopoverMenuButton onClick={onClearClick}>
              <FormattedMessage
                id="templates.workspace_page.clear-filter"
                defaultMessage="Clear filter..."
                description="The text of the popover button to clear the filter"
              />
            </PopoverMenuButton>
          </div>
        )}
        {props.selectProps?.isMemberOfOrg ? (
          <div className={styles.menuButton}>
            <PopoverMenuButton onClick={onCreateClick}>
              <FormattedMessage
                id="templates.workspace_page.create-a-new-collection"
                defaultMessage="Create a new collection"
                description="The text of the popover button to create a new collection"
              />
            </PopoverMenuButton>
          </div>
        ) : null}
      </>
    </SelectComponents.Menu>
  );
};

export interface CollectionsSelectUnwrappedProps {
  orgId: string;
  onSelect: (collection?: string) => void;
  collectionId?: string | null;
}

const CollectionsSelectUnwrapped: FunctionComponent<
  CollectionsSelectUnwrappedProps
> = ({ onSelect, orgId, collectionId }) => {
  const { isEligible } = useFreeTrialEligibilityRules(orgId);
  const { member } = useMyAdminRoles();
  const {
    data: orgMembershipData,
    loading: orgMembershipDataLoading,
    error: orgMembershipError,
  } = useOrgMembershipQuery({
    variables: {
      orgId,
    },
    waitOn: ['MemberHeader'],
  });

  const isMemberOfOrg: boolean =
    (!orgMembershipDataLoading &&
      member &&
      orgMembershipData?.organization &&
      isMemberOfOrganization(
        member,
        orgMembershipData.organization,
        orgMembershipData.organization.enterprise,
      )) ??
    false;

  const isAdminOfOrg = Boolean(
    member &&
      orgMembershipData?.organization &&
      isAdminOfOrganization(member, orgMembershipData.organization),
  );

  const isPublicOrg =
    orgMembershipData?.organization?.prefs.permissionLevel === 'public';

  const { data, error, loading } = useTagsQuery({
    variables: { orgId },
    skip: !(isMemberOfOrg || isPublicOrg),
    waitOn: ['MemberHeader', 'MemberBoards', 'WorkspaceBoardsPageMinimal'],
  });

  const tagsFeatureEnabled =
    !!data?.organization?.premiumFeatures?.includes('tags');
  // Use state to hide select menu only when clicking the clear filter button
  // Leave the default behavior for the rest of the options
  const [shouldCloseMenu, setShouldCloseMenu] = useState(false);
  const tagOptions = useMemo(
    () =>
      (data?.organization?.tags || [])
        ?.map((c) => ({ label: c.name, value: c.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data?.organization?.tags],
  );

  const orgHasNoCollections = useMemo(() => {
    return data && (tagOptions.length === 0 || !tagsFeatureEnabled);
  }, [data, tagOptions.length, tagsFeatureEnabled]);

  useEffect(() => {
    if (orgMembershipError) {
      sendErrorEvent(orgMembershipError, {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Collections',
        },
      });
    }
  }, [orgMembershipError]);

  useEffect(() => {
    if (error) {
      sendErrorEvent(error, {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Collections',
        },
      });
    }
  }, [error]);

  const { popoverProps, triggerRef, push, hide } = usePopover<HTMLElement>();

  const { popoverProps: soloCreatePopoverProps, toggle: soloCreateToggle } =
    usePopover<HTMLButtonElement>({
      initialScreen: CollectionsScreen.SoloCreateNewCollection,
    });

  const targetRef = useRef<HTMLDivElement>(null);
  const selectedOption =
    tagOptions?.find((c) => c.value === collectionId) || null;

  const onChange = (collection: CollectionOption | null) => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'dropdownItem',
      actionSubjectId: 'filterDropdownItem',
      source: 'workspaceBoardsScreen',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });

    onSelect(collection?.value);
    // When clicking the clear filter button (i.e. collection is null),
    // set the state of shouldCloseMenu to true to hide the menu
    if (collection === null) {
      setShouldCloseMenu(true);
    }
  };

  const onEmptyStateCreateClick = useCallback(() => {
    push(CollectionsScreen.CreateNewCollection);
  }, [push]);

  const onMenuOpen = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'dropdown',
      actionSubjectId: 'filterDropdown',
      source: 'workspaceBoardsScreen',
      containers: {
        organization: {
          id: orgId,
        },
      },
      attributes: {
        orgHasNoCollections,
        isFreeTrial: isEligible,
      },
    });
    setShouldCloseMenu(false);
  }, [orgId, orgHasNoCollections, setShouldCloseMenu, isEligible]);

  const onCreatedCollection = useCallback(() => {
    setShouldCloseMenu(false);
    hide();
  }, [setShouldCloseMenu, hide]);

  const onHide = useCallback(() => {
    if (
      popoverProps?.currentScreen === CollectionsScreen.EmptyState &&
      !tagsFeatureEnabled
    ) {
      Analytics.sendClosedComponentEvent({
        componentName: 'bcUpgradePrompt',
        componentType: 'upsell',
        source: 'workspaceBoardsScreen',
        containers: {
          organization: {
            id: orgId,
          },
        },
      });
    }

    hide();
  }, [popoverProps?.currentScreen, tagsFeatureEnabled, hide, orgId]);

  // Don't render upsells on desktop or to non-members
  if (!tagsFeatureEnabled && (dontUpsell() || !isMemberOfOrg)) {
    return null;
  }

  // Don't render to non-members unless the org is public
  if (!isMemberOfOrg && !isPublicOrg) {
    return null;
  }

  return (
    <div ref={targetRef}>
      <label htmlFor="filter-by" className={styles.label}>
        <FormattedMessage
          id="templates.workspace_page.filter-by"
          defaultMessage="Filter by"
          description="Filter by"
        />
      </label>
      <div ref={triggerRef}>
        <Select
          isLoading={loading || orgMembershipDataLoading}
          options={tagOptions}
          className={styles.select}
          value={selectedOption}
          menuPlacement="auto"
          components={{
            Menu,
          }}
          onMenuOpen={onMenuOpen}
          onChange={onChange}
          onCreateClick={() => soloCreateToggle()}
          onClearClick={() => {
            onChange(null);
          }}
          orgId={orgId}
          orgName={data?.organization?.name}
          testId={getTestId<TeamTestIds>('choose-a-collection')}
          placeholder={
            <FormattedMessage
              id="templates.workspace_page.choose-a-collection"
              defaultMessage="Choose a collection"
              description="The placeholder of the select to choose a collection"
            />
          }
          isMemberOfOrg={isMemberOfOrg}
          isAdminOfOrg={isAdminOfOrg}
          tagsFeatureEnabled={tagsFeatureEnabled}
          {...(error
            ? {
                noOptionsMessage: () => (
                  <FormattedMessage
                    id="templates.workspace_page.error-loading-collections"
                    defaultMessage="There was an error loading your collections."
                    description="The error message for loading your collections"
                  />
                ),
              }
            : {})}
          // When the state of shouldCloseMenu is true, hide the menu
          {...(shouldCloseMenu ? { menuIsOpen: false } : {})}
        />
      </div>
      <Popover
        {...popoverProps}
        onHide={onHide}
        targetElement={targetRef.current}
      >
        <PopoverScreen
          title={
            <FormattedMessage
              id="templates.workspace_page.collections"
              defaultMessage="Collections"
              description="The title of the popover screen"
            />
          }
          id={CollectionsScreen.EmptyState}
        >
          <EmptyState
            collectionsEnabled={tagsFeatureEnabled}
            onCreateClick={onEmptyStateCreateClick}
            orgId={orgId}
            orgName={data?.organization?.name}
            isMember={isMemberOfOrg}
            isAdmin={isAdminOfOrg}
          />
        </PopoverScreen>
        <PopoverScreen
          id={CollectionsScreen.CreateNewCollection}
          title={
            <FormattedMessage
              id="templates.workspace_page.create-a-new-collection"
              defaultMessage="Create a new collection"
              description="The title of the create a new collection popover"
            />
          }
        >
          <CreateNewCollection orgId={orgId} onCreate={onCreatedCollection} />
        </PopoverScreen>
      </Popover>
      <Popover {...soloCreatePopoverProps} targetElement={targetRef.current}>
        <PopoverScreen
          id={CollectionsScreen.SoloCreateNewCollection}
          title={
            <FormattedMessage
              id="templates.workspace_page.create-a-new-collection"
              defaultMessage="Create a new collection"
              description="The title of the create a new collection popover"
            />
          }
        >
          <CreateNewCollection orgId={orgId} onCreate={soloCreateToggle} />
        </PopoverScreen>
      </Popover>
    </div>
  );
};

export const CollectionsSelect = (props: CollectionsSelectUnwrappedProps) => (
  <ApolloProvider>
    <CollectionsSelectUnwrapped {...props} />
  </ApolloProvider>
);
